<template>
  <div class="conversations-view">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Conversations</h1>
        <p class="text-gray-600 mt-1">Monitor and manage all Telegram bot conversations</p>
      </div>
      <div class="flex gap-3">
        <button
          :disabled="loading"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          @click="refreshConversations"
        >
          <svg
            v-if="loading"
            class="animate-spin h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <svg
            v-else
            class="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </button>
        <button
          class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
          @click="showMetrics = !showMetrics"
        >
          <svg
            class="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          {{ showMetrics ? 'Hide' : 'Show' }} Metrics
        </button>
      </div>
    </div>

    <!-- WebSocket Status -->
    <div
      v-if="wsConnected"
      class="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2"
    >
      <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
      <span class="text-green-700 text-sm">Live updates connected</span>
    </div>

    <!-- Metrics Panel -->
    <div
      v-if="showMetrics && metrics"
      class="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
    >
      <div class="bg-white p-4 rounded-lg shadow border">
        <div class="text-sm text-gray-600">Total Conversations</div>
        <div class="text-2xl font-bold text-blue-600">
          {{ metrics.conversations?.totalConversations || 0 }}
        </div>
      </div>
      <div class="bg-white p-4 rounded-lg shadow border">
        <div class="text-sm text-gray-600">Active Conversations</div>
        <div class="text-2xl font-bold text-green-600">
          {{ metrics.conversations?.activeConversations || 0 }}
        </div>
      </div>
      <div class="bg-white p-4 rounded-lg shadow border">
        <div class="text-sm text-gray-600">Total Messages</div>
        <div class="text-2xl font-bold text-purple-600">
          {{ metrics.conversations?.totalMessages || 0 }}
        </div>
      </div>
      <div class="bg-white p-4 rounded-lg shadow border">
        <div class="text-sm text-gray-600">Tool Calls</div>
        <div class="text-2xl font-bold text-orange-600">{{ metrics.tools?.totalCalls || 0 }}</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="mb-6 bg-white p-4 rounded-lg shadow border">
      <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <input
            v-model="filters.searchTerm"
            type="text"
            placeholder="Search conversations..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            @input="debouncedFilter"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            v-model="filters.isActive"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            @change="applyFilters"
          >
            <option value="">All</option>
            <option :value="true">Active</option>
            <option :value="false">Inactive</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Date From</label>
          <input
            v-model="filters.dateFrom"
            type="date"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Date To</label>
          <input
            v-model="filters.dateTo"
            type="date"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Min Messages</label>
          <input
            v-model.number="filters.minMessages"
            type="number"
            min="0"
            placeholder="0"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            @input="debouncedFilter"
          />
        </div>
        <div class="flex items-end">
          <button
            class="w-full px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            @click="clearFilters"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>

    <!-- Conversations List -->
    <div class="bg-white rounded-lg shadow border">
      <div class="px-6 py-4 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">Conversations ({{ filteredTotal }})</h2>
      </div>

      <div v-if="loading && conversations.length === 0" class="p-8 text-center">
        <div
          class="animate-spin mx-auto h-8 w-8 text-blue-600"
          style="
            border: 2px solid;
            border-color: currentColor transparent transparent transparent;
            border-radius: 50%;
          "
        />
        <p class="mt-2 text-gray-600">Loading conversations...</p>
      </div>

      <div v-else-if="conversations.length === 0" class="p-8 text-center">
        <svg
          class="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <p class="mt-2 text-lg font-medium text-gray-900">No conversations found</p>
        <p class="text-gray-600">Start chatting with the Telegram bot to see conversations here.</p>
      </div>

      <div v-else>
        <div
          v-for="conversation in conversations"
          :key="conversation.chatId"
          class="px-6 py-4 border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
          @click="openConversation(conversation.chatId)"
        >
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <div class="flex items-center gap-3">
                <div class="flex-shrink-0">
                  <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span class="text-blue-600 font-medium">
                      {{
                        conversation.userName ? conversation.userName.charAt(0).toUpperCase() : '?'
                      }}
                    </span>
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <p class="text-sm font-medium text-gray-900 truncate">
                      {{ conversation.userName || `User ${conversation.userId}` }}
                    </p>
                    <span
                      :class="[
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        conversation.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800',
                      ]"
                    >
                      {{ conversation.isActive ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                  <div class="flex items-center gap-4 mt-1 text-sm text-gray-600">
                    <span>{{ conversation.messageCount }} messages</span>
                    <span v-if="conversation.toolsUsed && conversation.toolsUsed.length">
                      {{ conversation.toolsUsed.length }} tools used
                    </span>
                    <span v-if="conversation.duration">
                      {{
                        formatDuration(
                          conversation.duration.hours * 60 + conversation.duration.minutes
                        )
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <div class="text-right">
                <p class="text-sm text-gray-900">
                  {{ formatTimestamp(conversation.lastActivity) }}
                </p>
                <p class="text-xs text-gray-500">
                  {{ formatTimestamp(conversation.conversationStarted) }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <button
                  class="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  title="View conversation"
                  @click.stop="openConversation(conversation.chatId)"
                >
                  <svg
                    class="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </button>
                <button
                  class="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete conversation"
                  @click.stop="deleteConversation(conversation.chatId)"
                >
                  <svg
                    class="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Topics and Tools -->
          <div
            v-if="conversation.topics || conversation.toolsUsed"
            class="mt-3 flex flex-wrap gap-2"
          >
            <span
              v-for="topic in conversation.topics?.slice(0, 3)"
              :key="topic"
              class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
            >
              {{ topic }}
            </span>
            <span
              v-for="tool in conversation.toolsUsed?.slice(0, 3)"
              :key="tool"
              class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800"
            >
              🔧 {{ tool }}
            </span>
            <span
              v-if="(conversation.topics?.length || 0) + (conversation.toolsUsed?.length || 0) > 6"
              class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600"
            >
              +{{ (conversation.topics?.length || 0) + (conversation.toolsUsed?.length || 0) - 6 }}
              more
            </span>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="px-6 py-4 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-700">
              Showing {{ (currentPage - 1) * pageSize + 1 }} to
              {{ Math.min(currentPage * pageSize, filteredTotal) }} of
              {{ filteredTotal }} conversations
            </div>
            <div class="flex items-center gap-2">
              <button
                :disabled="currentPage === 1"
                class="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="goToPage(currentPage - 1)"
              >
                Previous
              </button>
              <button
                v-for="page in visiblePages"
                :key="page"
                :class="[
                  'px-3 py-2 text-sm border rounded-md',
                  page === currentPage
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                ]"
                @click="goToPage(page)"
              >
                {{ page }}
              </button>
              <button
                :disabled="currentPage === totalPages"
                class="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="goToPage(currentPage + 1)"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { conversationService } from '../services/conversationService';
import type {
  ConversationSummary,
  ConversationFilter,
  ConversationMetrics,
  ToolCallMetrics,
  WebSocketMessage,
} from '../types/conversation';

const router = useRouter();

// Reactive state
const conversations = ref<ConversationSummary[]>([]);
const loading = ref(false);
const wsConnected = ref(false);
const showMetrics = ref(false);
const metrics = ref<{
  conversations: ConversationMetrics | null;
  tools: ToolCallMetrics | null;
  timestamp: string;
} | null>(null);

// Pagination
const currentPage = ref(1);
const pageSize = ref(20);
const filteredTotal = ref(0);

// Filters
const filters = ref<ConversationFilter>({
  searchTerm: '',
  isActive: undefined,
  dateFrom: '',
  dateTo: '',
  minMessages: undefined,
});

// Computed properties
const totalPages = computed(() => Math.ceil(filteredTotal.value / pageSize.value));

const visiblePages = computed(() => {
  const pages = [];
  const start = Math.max(1, currentPage.value - 2);
  const end = Math.min(totalPages.value, currentPage.value + 2);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return pages;
});

// Methods
const loadConversations = async () => {
  loading.value = true;
  try {
    const response = await conversationService.getConversations(
      pageSize.value,
      (currentPage.value - 1) * pageSize.value,
      filters.value
    );
    conversations.value = response.conversations;
    filteredTotal.value = response.total;
  } catch (error) {
    console.error('Failed to load conversations:', error);
  } finally {
    loading.value = false;
  }
};

const loadMetrics = async () => {
  try {
    metrics.value = await conversationService.getMetrics();
  } catch (error) {
    console.error('Failed to load metrics:', error);
  }
};

const refreshConversations = async () => {
  await loadConversations();
  if (showMetrics.value) {
    await loadMetrics();
  }
};

const applyFilters = () => {
  currentPage.value = 1;
  loadConversations();
};

const clearFilters = () => {
  filters.value = {
    searchTerm: '',
    isActive: undefined,
    dateFrom: '',
    dateTo: '',
    minMessages: undefined,
  };
  applyFilters();
};

// Debounced filter for search and numeric inputs
let filterTimeout: ReturnType<typeof setTimeout> | null = null;
const debouncedFilter = () => {
  if (filterTimeout) clearTimeout(filterTimeout);
  filterTimeout = setTimeout(() => {
    applyFilters();
  }, 500);
};

const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    loadConversations();
  }
};

const openConversation = (chatId: string) => {
  router.push(`/conversations/${chatId}`);
};

const deleteConversation = async (chatId: string) => {
  if (
    !confirm('Are you sure you want to delete this conversation? This action cannot be undone.')
  ) {
    return;
  }

  try {
    await conversationService.deleteConversation(chatId);
    await refreshConversations();
  } catch (error) {
    console.error('Failed to delete conversation:', error);
    alert('Failed to delete conversation. Please try again.');
  }
};

const formatDuration = (minutes: number): string => {
  return conversationService.formatDuration(minutes);
};

const formatTimestamp = (timestamp: string): string => {
  return conversationService.formatTimestamp(timestamp);
};

// WebSocket handlers
const handleWebSocketMessage = (message: WebSocketMessage) => {
  if (message.type === 'conversation_update') {
    // Refresh conversations on updates
    loadConversations();
  }
};

const connectWebSocket = () => {
  conversationService.connectWebSocket(
    handleWebSocketMessage,
    () => {
      wsConnected.value = true;
      conversationService.subscribeToAll();
    },
    () => {
      wsConnected.value = false;
    },
    error => {
      console.error('WebSocket error:', error);
      wsConnected.value = false;
    }
  );
};

// Lifecycle
onMounted(async () => {
  await loadConversations();
  await loadMetrics();
  connectWebSocket();
});

onUnmounted(() => {
  conversationService.disconnectWebSocket();
  if (filterTimeout) clearTimeout(filterTimeout);
});

// Watch for metrics visibility changes
watch(showMetrics, show => {
  if (show && !metrics.value) {
    loadMetrics();
  }
});
</script>

<style scoped>
.conversations-view {
  @apply max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6;
}
</style>
